import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription, CardFooter } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Loader2, Search, Code, FileText, Play, Save, AlertCircle, CheckCircle2 } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { useSparcAgent } from '@/hooks/sparc/useSparcAgent';
import { toast } from 'sonner';
import { useCapabilityPermission } from '@/hooks/useCapabilityPermissions';

const CodeAnalyzer = () => {
  const [files, setFiles] = useState<string>('');
  const [task, setTask] = useState<string>('');
  const [code, setCode] = useState<string>('');
  const [language, setLanguage] = useState<string>('javascript');
  const [searchQuery, setSearchQuery] = useState<string>('');
  
  const { hasPermission } = useCapabilityPermission('analysis-capability-id', 'execute');
  
  const {
    loading,
    analyzing,
    modifying,
    executing,
    searching,
    analysisResult,
    modificationResult,
    executionResult,
    searchResult,
    analyze,
    modify,
    execute,
    search,
    config,
    updateConfig
  } = useSparcAgent();
  
  const handleAnalyze = async () => {
    if (!files.trim()) {
      toast.error('Please specify at least one file to analyze');
      return;
    }
    
    try {
      const filesList = files.split(',').map(f => f.trim());
      await analyze(filesList, task || undefined);
      toast.success('Code analysis completed');
    } catch (error) {
      console.error('Analysis error:', error);
    }
  };
  
  const handleModify = async () => {
    if (!files.trim() || !task.trim()) {
      toast.error('Please specify files and a task for modification');
      return;
    }
    
    try {
      const filesList = files.split(',').map(f => f.trim());
      await modify(filesList, task);
      toast.success('Code modification completed');
    } catch (error) {
      console.error('Modification error:', error);
    }
  };
  
  const handleExecute = async () => {
    if (!code.trim()) {
      toast.error('Please enter code to execute');
      return;
    }
    
    try {
      await execute(code, language);
      toast.success('Code execution completed');
    } catch (error) {
      console.error('Execution error:', error);
    }
  };
  
  const handleSearch = async () => {
    if (!searchQuery.trim()) {
      toast.error('Please enter a search query');
      return;
    }
    
    try {
      await search(searchQuery, 5);
      toast.success('Code search completed');
    } catch (error) {
      console.error('Search error:', error);
    }
  };
  
  const renderExecutionOutput = () => {
    if (!executionResult) return null;
    
    return (
      <div className="mt-4 space-y-2">
        <div className="border rounded-md p-4 bg-black text-white font-mono text-sm overflow-auto max-h-60">
          {executionResult.stdout.map((line, idx) => (
            <div key={idx}>{line}</div>
          ))}
          {executionResult.stderr.map((line, idx) => (
            <div key={idx} className="text-red-400">{line}</div>
          ))}
          {executionResult.error && (
            <div className="text-red-400">{executionResult.error}</div>
          )}
        </div>
      </div>
    );
  };
  
  if (!hasPermission) {
    return (
      <Card className="w-full">
        <CardHeader>
          <CardTitle className="flex items-center">
            <AlertCircle className="mr-2 h-5 w-5 text-amber-500" />
            Permission Required
          </CardTitle>
          <CardDescription>
            You don't have permission to use the code analysis features
          </CardDescription>
        </CardHeader>
        <CardContent>
          <p>Contact an administrator to request access to the SPARC code analysis tools.</p>
        </CardContent>
      </Card>
    );
  }
  
  return (
    <Card className="w-full">
      <CardHeader>
        <CardTitle className="flex items-center">
          <Code className="mr-2 h-5 w-5 text-primary" />
          SPARC Code Analysis
        </CardTitle>
        <CardDescription>
          Analyze, modify, and test your code with AI assistance
        </CardDescription>
      </CardHeader>
      <CardContent>
        <Tabs defaultValue="analyze" className="space-y-4">
          <TabsList className="grid grid-cols-4 gap-4">
            <TabsTrigger value="analyze" className="flex items-center">
              <Search className="mr-2 h-4 w-4" />
              Analyze
            </TabsTrigger>
            <TabsTrigger value="modify" className="flex items-center">
              <Code className="mr-2 h-4 w-4" />
              Modify
            </TabsTrigger>
            <TabsTrigger value="execute" className="flex items-center">
              <Play className="mr-2 h-4 w-4" />
              Execute
            </TabsTrigger>
            <TabsTrigger value="search" className="flex items-center">
              <FileText className="mr-2 h-4 w-4" />
              Search
            </TabsTrigger>
          </TabsList>
          
          <TabsContent value="analyze" className="space-y-4">
            <div className="space-y-4">
              <div>
                <Label htmlFor="files">Files to Analyze (comma-separated)</Label>
                <Input
                  id="files"
                  value={files}
                  onChange={(e) => setFiles(e.target.value)}
                  placeholder="src/components/Button.tsx, src/hooks/useAuth.ts"
                />
              </div>
              
              <div>
                <Label htmlFor="task">Analysis Task (optional)</Label>
                <Textarea
                  id="task"
                  value={task}
                  onChange={(e) => setTask(e.target.value)}
                  placeholder="Check for performance issues and suggest improvements"
                  rows={3}
                />
              </div>
              
              <Button 
                onClick={handleAnalyze} 
                disabled={analyzing || !files.trim()}
                className="w-full"
              >
                {analyzing ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Analyzing...
                  </>
                ) : (
                  <>
                    <Search className="mr-2 h-4 w-4" />
                    Analyze Code
                  </>
                )}
              </Button>
              
              {analysisResult && (
                <div className="space-y-4 mt-4 p-4 border rounded-md">
                  <h3 className="text-lg font-medium">Analysis Results</h3>
                  
                  <div>
                    <h4 className="text-sm font-medium mb-2">Summary</h4>
                    <p>{analysisResult.summary}</p>
                  </div>
                  
                  <div>
                    <h4 className="text-sm font-medium mb-2">Issues</h4>
                    {analysisResult.issues.length > 0 ? (
                      <ul className="space-y-2">
                        {analysisResult.issues.map((issue, idx) => (
                          <li key={idx} className="flex items-start space-x-2">
                            <Badge variant={issue.type === 'error' ? 'destructive' : issue.type === 'warning' ? 'default' : 'secondary'}>
                              {issue.type}
                            </Badge>
                            <div>
                              <p>{issue.message}</p>
                              {issue.location && (
                                <p className="text-xs text-muted-foreground">
                                  {issue.location.file}
                                  {issue.location.line && `:${issue.location.line}`}
                                  {issue.location.column && `:${issue.location.column}`}
                                </p>
                              )}
                            </div>
                          </li>
                        ))}
                      </ul>
                    ) : (
                      <p className="text-sm text-muted-foreground">No issues found</p>
                    )}
                  </div>
                  
                  <div>
                    <h4 className="text-sm font-medium mb-2">Suggestions</h4>
                    {analysisResult.suggestions.length > 0 ? (
                      <ul className="space-y-2">
                        {analysisResult.suggestions.map((suggestion, idx) => (
                          <li key={idx} className="flex items-start space-x-2">
                            <CheckCircle2 className="h-4 w-4 text-green-500 mt-0.5" />
                            <p>{suggestion}</p>
                          </li>
                        ))}
                      </ul>
                    ) : (
                      <p className="text-sm text-muted-foreground">No suggestions</p>
                    )}
                  </div>
                </div>
              )}
            </div>
          </TabsContent>
          
          <TabsContent value="modify" className="space-y-4">
            <div className="space-y-4">
              <div>
                <Label htmlFor="modifyFiles">Files to Modify (comma-separated)</Label>
                <Input
                  id="modifyFiles"
                  value={files}
                  onChange={(e) => setFiles(e.target.value)}
                  placeholder="src/components/Button.tsx, src/hooks/useAuth.ts"
                />
              </div>
              
              <div>
                <Label htmlFor="modifyTask">Modification Task</Label>
                <Textarea
                  id="modifyTask"
                  value={task}
                  onChange={(e) => setTask(e.target.value)}
                  placeholder="Fix memory leak by cleaning up event listeners"
                  rows={3}
                />
              </div>
              
              <Button 
                onClick={handleModify} 
                disabled={modifying || !files.trim() || !task.trim()}
                className="w-full"
              >
                {modifying ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Modifying...
                  </>
                ) : (
                  <>
                    <Code className="mr-2 h-4 w-4" />
                    Modify Code
                  </>
                )}
              </Button>
              
              {modificationResult && (
                <div className="space-y-4 mt-4 p-4 border rounded-md">
                  <h3 className="text-lg font-medium">Modification Results</h3>
                  
                  <div>
                    <h4 className="text-sm font-medium mb-2">Summary</h4>
                    <p>{modificationResult.summary}</p>
                  </div>
                  
                  <div>
                    <h4 className="text-sm font-medium mb-2">Changes</h4>
                    {modificationResult.changes.length > 0 ? (
                      <ul className="space-y-4">
                        {modificationResult.changes.map((change, idx) => (
                          <li key={idx} className="space-y-2">
                            <div className="flex items-center justify-between">
                              <p className="font-medium">{change.file}</p>
                              <Badge variant="outline">{change.summary}</Badge>
                            </div>
                            <pre className="p-2 bg-slate-950 text-slate-50 rounded-md text-xs overflow-auto">
                              {change.diff}
                            </pre>
                          </li>
                        ))}
                      </ul>
                    ) : (
                      <p className="text-sm text-muted-foreground">No changes made</p>
                    )}
                  </div>
                </div>
              )}
            </div>
          </TabsContent>
          
          <TabsContent value="execute" className="space-y-4">
            <div className="space-y-4">
              <div className="flex items-center space-x-2">
                <div className="flex-1">
                  <Label htmlFor="language">Language</Label>
                  <Select
                    value={language}
                    onValueChange={setLanguage}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select language" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="javascript">JavaScript</SelectItem>
                      <SelectItem value="typescript">TypeScript</SelectItem>
                      <SelectItem value="python">Python</SelectItem>
                      <SelectItem value="go">Go</SelectItem>
                      <SelectItem value="rust">Rust</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              
              <div>
                <Label htmlFor="codeInput">Code to Execute</Label>
                <Textarea
                  id="codeInput"
                  value={code}
                  onChange={(e) => setCode(e.target.value)}
                  placeholder="console.log('Hello, world!');"
                  rows={6}
                  className="font-mono"
                />
              </div>
              
              <Button 
                onClick={handleExecute} 
                disabled={executing || !code.trim()}
                className="w-full"
              >
                {executing ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Executing...
                  </>
                ) : (
                  <>
                    <Play className="mr-2 h-4 w-4" />
                    Execute Code
                  </>
                )}
              </Button>
              
              {renderExecutionOutput()}
            </div>
          </TabsContent>
          
          <TabsContent value="search" className="space-y-4">
            <div className="space-y-4">
              <div>
                <Label htmlFor="searchQuery">Search Query</Label>
                <Input
                  id="searchQuery"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Fix performance issue in rendering"
                />
              </div>
              
              <Button 
                onClick={handleSearch} 
                disabled={searching || !searchQuery.trim()}
                className="w-full"
              >
                {searching ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Searching...
                  </>
                ) : (
                  <>
                    <Search className="mr-2 h-4 w-4" />
                    Search Code
                  </>
                )}
              </Button>
              
              {searchResult && (
                <div className="space-y-4 mt-4 p-4 border rounded-md">
                  <h3 className="text-lg font-medium">Search Results</h3>
                  
                  {searchResult.matches.length > 0 ? (
                    <ul className="space-y-4">
                      {searchResult.matches.map((match, idx) => (
                        <li key={idx} className="space-y-2">
                          <div className="flex items-center justify-between">
                            <p className="font-medium">{match.file}</p>
                            <Badge variant="outline">Score: {Math.round(match.score * 100)}%</Badge>
                          </div>
                          {match.context && (
                            <p className="text-sm text-muted-foreground">{match.context}</p>
                          )}
                          <pre className="p-2 bg-slate-950 text-slate-50 rounded-md text-xs overflow-auto">
                            {match.snippet}
                          </pre>
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <p className="text-sm text-muted-foreground">No matches found</p>
                  )}
                </div>
              )}
            </div>
          </TabsContent>
        </Tabs>
      </CardContent>
      <CardFooter className="flex justify-between border-t pt-4">
        <div className="flex space-x-2">
          <Select
            value={config.executionMode || 'semi'}
            onValueChange={(value) => updateConfig({ executionMode: value as any })}
          >
            <SelectTrigger className="w-32">
              <SelectValue placeholder="Mode" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="automatic">Automatic</SelectItem>
              <SelectItem value="semi">Semi-Auto</SelectItem>
              <SelectItem value="manual">Manual</SelectItem>
              <SelectItem value="interactive">Interactive</SelectItem>
            </SelectContent>
          </Select>
          
          <Select
            value={config.diffMode || 'file'}
            onValueChange={(value) => updateConfig({ diffMode: value as any })}
          >
            <SelectTrigger className="w-32">
              <SelectValue placeholder="Diff Mode" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="file">File</SelectItem>
              <SelectItem value="function">Function</SelectItem>
            </SelectContent>
          </Select>
        </div>
        
        <Button variant="outline" onClick={() => toast.info('SPARC configuration saved')}>
          <Save className="mr-2 h-4 w-4" />
          Save Config
        </Button>
      </CardFooter>
    </Card>
  );
};

export default CodeAnalyzer;
